/**
 * Modal de detalhes da OS — layout V2 (validação; convive com OrderDetailsModal).
 */

import React from 'react';
import {
  Info,
  Wrench,
  TriangleAlert,
  MapPin,
  AlertTriangle,
  Map,
  ChevronDown,
  ChevronUp,
  Pencil,
  X,
  CalendarSearch,
  Eye,
  EyeOff,
  History,
  LandPlot,
} from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { hasDefeitoAlert } from '../utils/defeitoAlertBadge';
import { getDefeitoDisplayState } from '../utils/orderDetailsDefeitoDisplay';
import { getBoardClienteExibicao } from '../utils/clienteBoardDisplay';
import {
  formatSeverityLabel,
  contrastTextOnHex,
  getSLAStatus,
  buildSlaStatusMessage,
  getSlaTimeProgress,
  formatPhoneDisplay,
} from '../utils/orderDetailsModalUtils';
import { formatOrderStreetAddress, parseOrderAddressLines } from '../utils/orderDetailsAddressUtils';
import { useOrderDetailsAuditLogs } from '../hooks/useOrderDetailsAuditLogs';
import { useOrderDetailsHistorico } from '../hooks/useOrderDetailsHistorico';
import { useOrderDetailsNotes } from '../hooks/useOrderDetailsNotes';
import { useOrderDetailsDefeitoMotivoEdit } from '../hooks/useOrderDetailsDefeitoMotivoEdit';
import { useOrderDetailsTipoOsEdit } from '../hooks/useOrderDetailsTipoOsEdit';
import { useOrderDetailsReincidenciaHistorico } from '../hooks/useOrderDetailsReincidenciaHistorico';
import { useOrderDetailsPedidoPanel } from '../hooks/useOrderDetailsPedidoPanel';
import { OrderDetailsModalV2HeaderMenu, OrderDetailsModalV2FloatingFooter } from './OrderDetailsModalV2Actions';
import { returnOrderToPendente, fetchOrderAttachments } from '../services/boardOrderService';
import { OrderDetailsHistoricoProcessoModal } from './OrderDetailsHistoricoProcessoModal';
import { OrderDetailsAuditLogsModal } from './OrderDetailsAuditLogsModal';
import { OrderDetailsSugestaoModal } from './OrderDetailsSugestaoModal';
import { OrderDetailsAnexosModal } from './OrderDetailsAnexosModal';
import { OrderDetailsDefeitoAudio } from './OrderDetailsDefeitoAudio';
import { OrderDetailsEncaminharChamadoModal } from './OrderDetailsEncaminharChamadoModal';
import { OrderDetailsReincidenciaPanel } from './OrderDetailsReincidenciaPanel';
import { OrderDetailsPedidoPanel } from './OrderDetailsPedidoPanel';
import OpenOsV2EquipmentHistoryOverlay from '../../new-os/components/OpenOsV2EquipmentHistoryOverlay';
import { OrderDetailsTecnicosTerritorioModal } from './OrderDetailsTecnicosTerritorioModal';
import { Odv2HoverTooltip } from './Odv2HoverTooltip';
import { OrderDetailsPrevisaoTimer } from './OrderDetailsPrevisaoTimer';
import { OrderDetailsNotesColPanel } from './OrderDetailsNotesColPanel';
import '@fontsource-variable/geist/wght.css';
import '@fontsource/geist-mono/400.css';
import '@fontsource/geist-mono/500.css';
import '@fontsource/geist-mono/600.css';
import '@fontsource/geist-mono/700.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './OrderDetailsModalV2.css';

const ATENDIMENTOS_HISTORICO_ANIM_MS = 280;

export default function OrderDetailsModalV2({
  order,
  onClose,
  codstatusRetorno,
  statusUptodate = '',
  onDadosEquipamento,
  onEncaminharChamado,
  canEncaminharChamado = false,
  encaminharPanel = null,
  onEncaminharPanelClose,
  onEncaminharConfirm,
  onMotivoUpdated,
  onTipoUpdated,
  isAdminUser = false,
  hasCustomersModule = false,
  onReturnToPendenteSuccess,
}) {
  const { showLogsView, auditLogs, loadingAuditLogs, auditLogsError, openLogsView, closeLogsView } =
    useOrderDetailsAuditLogs(order);
  const {
    showHistoricoView,
    historicoSteps,
    historicoCycles,
    loadingHistorico,
    historicoError,
    openHistoricoView,
    closeHistoricoView,
  } = useOrderDetailsHistorico(order);
  const {
    notes: orderNotes,
    hasNotes,
    loadingNotes,
    savingNote,
    notesError,
    saveError,
    fetchNotes,
    submitNote,
  } = useOrderDetailsNotes(order);

  const [showDefeitoDetails, setShowDefeitoDetails] = React.useState(false);
  const [defeitoData, setDefeitoData] = React.useState(null);
  const [loadingDefeito, setLoadingDefeito] = React.useState(false);
  const [defeitoError, setDefeitoError] = React.useState(null);
  const [tecnicosTerritorioModalOpen, setTecnicosTerritorioModalOpen] = React.useState(false);
  const [tecnicosTerritorio, setTecnicosTerritorio] = React.useState(null);
  const [returnToPendenteBusy, setReturnToPendenteBusy] = React.useState(false);
  const [returnToPendenteFeedback, setReturnToPendenteFeedback] = React.useState(null);
  const [loadingTecnicosTerritorio, setLoadingTecnicosTerritorio] = React.useState(false);
  const [tecnicosTerritorioError, setTecnicosTerritorioError] = React.useState(null);
  const [linkedPedidosList, setLinkedPedidosList] = React.useState([]);
  const [loadingLinkedPedidos, setLoadingLinkedPedidos] = React.useState(false);
  const [solicitanteEmail, setSolicitanteEmail] = React.useState(null);
  const [showSugestaoSolucao, setShowSugestaoSolucao] = React.useState(false);
  const [showAnexosView, setShowAnexosView] = React.useState(false);
  const [anexosFiles, setAnexosFiles] = React.useState([]);
  const [anexosLoading, setAnexosLoading] = React.useState(false);
  const [anexosError, setAnexosError] = React.useState(null);
  const [standbyNotesPopoverOpen, setStandbyNotesPopoverOpen] = React.useState(false);
  const [showReincidenciaPanel, setShowReincidenciaPanel] = React.useState(false);
  const [pedidoPanelCodigo, setPedidoPanelCodigo] = React.useState(null);
  const [atendimentosHistoricoOpen, setAtendimentosHistoricoOpen] = React.useState(false);
  const [atendimentosHistoricoMounted, setAtendimentosHistoricoMounted] = React.useState(false);
  const [notesPanelOpen, setNotesPanelOpen] = React.useState(false);
  const [notesComposeOnOpen, setNotesComposeOnOpen] = React.useState(false);
  const atendimentosHistoricoCloseTimerRef = React.useRef(null);
  const notesPanelWasOpenBeforeHistoricoRef = React.useRef(false);

  const closeAtendimentosHistorico = React.useCallback(() => {
    setAtendimentosHistoricoOpen(false);
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
    }
    atendimentosHistoricoCloseTimerRef.current = setTimeout(() => {
      setAtendimentosHistoricoMounted(false);
      if (notesPanelWasOpenBeforeHistoricoRef.current) {
        notesPanelWasOpenBeforeHistoricoRef.current = false;
        setNotesComposeOnOpen(false);
        setNotesPanelOpen(true);
      }
      atendimentosHistoricoCloseTimerRef.current = null;
    }, ATENDIMENTOS_HISTORICO_ANIM_MS);
  }, []);

  const openAtendimentosHistorico = React.useCallback(() => {
    setPedidoPanelCodigo(null);
    setShowReincidenciaPanel(false);
    setNotesPanelOpen((wasOpen) => {
      notesPanelWasOpenBeforeHistoricoRef.current = wasOpen;
      return false;
    });
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
      atendimentosHistoricoCloseTimerRef.current = null;
    }
    setAtendimentosHistoricoMounted(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setAtendimentosHistoricoOpen(true));
    });
  }, []);

  const handleHistoricoServicoClick = React.useCallback(
    (e) => {
      e.stopPropagation();
      if (atendimentosHistoricoMounted) {
        closeAtendimentosHistorico();
      } else {
        openAtendimentosHistorico();
      }
    },
    [atendimentosHistoricoMounted, closeAtendimentosHistorico, openAtendimentosHistorico],
  );

  const osAnteriorForReincidencia =
    order?.TB02115_OS ?? order?.tb02115_os ?? order?.TB02115_os;
  const isOrderReincidencia = String(osAnteriorForReincidencia ?? '').trim().length > 0;
  const {
    items: reincidenciaItems,
    loading: loadingReincidencia,
    error: reincidenciaError,
  } = useOrderDetailsReincidenciaHistorico(
    order?.id,
    Boolean(showReincidenciaPanel && isOrderReincidencia),
  );

  const {
    pedido: pedidoPanelData,
    statusAtual: pedidoStatusAtual,
    loading: loadingPedidoPanel,
    error: pedidoPanelError,
  } = useOrderDetailsPedidoPanel(pedidoPanelCodigo, Boolean(pedidoPanelCodigo), order?.source === 'native');

  React.useEffect(() => {
    setShowReincidenciaPanel(false);
    setPedidoPanelCodigo(null);
    setShowAnexosView(false);
    setAnexosFiles([]);
    setAnexosError(null);
    setAtendimentosHistoricoOpen(false);
    setAtendimentosHistoricoMounted(false);
    notesPanelWasOpenBeforeHistoricoRef.current = false;
    if (atendimentosHistoricoCloseTimerRef.current) {
      clearTimeout(atendimentosHistoricoCloseTimerRef.current);
      atendimentosHistoricoCloseTimerRef.current = null;
    }
  }, [order?.id]);

  React.useEffect(() => {
    const orderId = order?.id ?? order?.TB02115_CODIGO;
    if (!hasCustomersModule || !orderId) {
      setAnexosFiles([]);
      setAnexosError(null);
      setAnexosLoading(false);
      return undefined;
    }
    let cancelled = false;
    setAnexosLoading(true);
    fetchOrderAttachments(orderId).then((res) => {
      if (cancelled) return;
      if (res.success) {
        setAnexosFiles(res.data);
        setAnexosError(null);
        const hasVisual = (res.data || []).some((f) => !f.isAudio);
        if (!hasVisual) setShowAnexosView(false);
      } else {
        setAnexosFiles([]);
        setAnexosError(res.message || 'Erro ao carregar anexos');
      }
      setAnexosLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [hasCustomersModule, order?.id, order?.TB02115_CODIGO]);

  const defeitoMotivoEdit = useOrderDetailsDefeitoMotivoEdit(order, { onMotivoUpdated });
  const tipoOsEdit = useOrderDetailsTipoOsEdit(order, { isAdmin: isAdminUser, onTipoUpdated });
  const {
    motivoDisplay,
    isMotivoEditing,
    motivoEditValue,
    setMotivoEditValue,
    isSavingMotivo,
    motivoSaveFeedback,
    handleStartMotivoEdit,
    handleCancelMotivoEdit,
    handleSaveMotivo,
    MOTIVO_OS_MAX,
    isDefeitoEditing,
    isObsEditing,
    defeitoSearchQuery,
    defeitoSearchResults,
    isDefeitoSearchLoading,
    defeitoSearchError,
    editObsValue,
    setEditObsValue,
    isSavingDefeitoObs,
    defeitoSaveFeedback,
    handleStartDefeitoEdit,
    handleStartObsEdit,
    handleDefeitoSearchChange,
    handleSelectDefeitoResult,
    handleCancelDefeitoObsEdit,
    handleSaveDefeitoObs,
    isObsGeralEditing,
    editObsGeralValue,
    setEditObsGeralValue,
    isSavingObsGeral,
    obsGeralSaveFeedback,
    handleStartObsGeralEdit,
    handleCancelObsGeralEdit,
    handleSaveObsGeral,
  } = defeitoMotivoEdit;
  const standbyNotesAnchorRef = React.useRef(null);

  const openNotesPanel = React.useCallback(() => {
    setShowReincidenciaPanel(false);
    setPedidoPanelCodigo(null);
    if (atendimentosHistoricoMounted) closeAtendimentosHistorico();
    setNotesComposeOnOpen(false);
    setNotesPanelOpen(true);
  }, [atendimentosHistoricoMounted, closeAtendimentosHistorico]);

  const openNotesPanelForCompose = React.useCallback(() => {
    setShowReincidenciaPanel(false);
    setPedidoPanelCodigo(null);
    if (atendimentosHistoricoMounted) closeAtendimentosHistorico();
    setNotesComposeOnOpen(true);
    setNotesPanelOpen(true);
  }, [atendimentosHistoricoMounted, closeAtendimentosHistorico]);

  const closeNotesPanel = React.useCallback(() => {
    setNotesPanelOpen(false);
    setNotesComposeOnOpen(false);
  }, []);

  React.useEffect(() => {
    setShowSugestaoSolucao(false);
  }, [order?.id]);

  React.useEffect(() => {
    setStandbyNotesPopoverOpen(false);
    setNotesPanelOpen(false);
    setNotesComposeOnOpen(false);
  }, [order?.id]);

  React.useEffect(() => {
    if (!order?.id) return undefined;
    let cancelled = false;
    (async () => {
      const list = await fetchNotes();
      if (cancelled) return;
      if (list.length > 0) {
        setShowReincidenciaPanel(false);
        setPedidoPanelCodigo(null);
        setNotesComposeOnOpen(false);
        setNotesPanelOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?.id, fetchNotes]);

  React.useEffect(() => {
    if (!order || !(Number(order.pedidos_vinculados) > 1)) {
      setLinkedPedidosList([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingLinkedPedidos(true);
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/orders/${order.id}/linked-pedidos`, {
      headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success && Array.isArray(data.data?.pedidos)) {
          setLinkedPedidosList(data.data.pedidos);
        } else if (!cancelled) {
          setLinkedPedidosList([]);
        }
      })
      .catch(() => {
        if (!cancelled) setLinkedPedidosList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLinkedPedidos(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.id, order?.pedidos_vinculados]);

  React.useEffect(() => {
    setSolicitanteEmail(null);
    if (!order?.id) return undefined;
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    fetch(`${getApiBaseUrl()}/api/orders/${order.id}/solicitante-email`, {
      headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;
        const em = data?.success && data?.data?.email ? String(data.data.email).trim() : '';
        setSolicitanteEmail(em || null);
      })
      .catch(() => {
        if (!cancelled) setSolicitanteEmail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [order?.id]);

  React.useEffect(() => {
    if (!order?.id) return undefined;
    setDefeitoData(null);
    setDefeitoError(null);
    let cancelled = false;
    (async () => {
      setLoadingDefeito(true);
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/defeito`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        const result = await response.json();
        if (cancelled) return;
        if (result.success) setDefeitoData(result.data);
        else setDefeitoError(result.message);
      } catch {
        if (!cancelled) setDefeitoError('Erro ao conectar com o servidor');
      } finally {
        if (!cancelled) setLoadingDefeito(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [order?.id]);

  const fetchTecnicosTerritorio = React.useCallback(async () => {
    if (!order?.id || tecnicosTerritorio) return;
    setLoadingTecnicosTerritorio(true);
    setTecnicosTerritorioError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiBaseUrl()}/api/orders/${order.id}/tecnicos-territorio`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      if (result.success) setTecnicosTerritorio(result.data);
      else setTecnicosTerritorioError(result.message);
    } catch {
      setTecnicosTerritorioError('Erro ao conectar com o servidor');
    } finally {
      setLoadingTecnicosTerritorio(false);
    }
  }, [order?.id, tecnicosTerritorio]);

  const openTecnicosTerritorioModal = React.useCallback(() => {
    setTecnicosTerritorioModalOpen(true);
    fetchTecnicosTerritorio();
  }, [fetchTecnicosTerritorio]);

  const closeTecnicosTerritorioModal = React.useCallback(() => {
    setTecnicosTerritorioModalOpen(false);
  }, []);

  const handleReturnToPendente = React.useCallback(async () => {
    const numeroOS = String(order?.id ?? order?.TB02115_CODIGO ?? '').trim();
    if (!numeroOS || returnToPendenteBusy) return;
    setReturnToPendenteFeedback(null);
    setReturnToPendenteBusy(true);
    try {
      const result = await returnOrderToPendente(numeroOS);
      if (!result.success) {
        setReturnToPendenteFeedback({
          type: 'error',
          message: result.message || 'Não foi possível retornar a OS para pendência.',
        });
        return;
      }
      setReturnToPendenteFeedback({
        type: 'success',
        message: result.message || 'OS retornada para pendência com sucesso.',
      });
      if (typeof onReturnToPendenteSuccess === 'function') {
        await onReturnToPendenteSuccess({
          orderId: numeroOS,
          technician: order?.detailModalBoardTechnician || null,
        });
      }
    } catch (err) {
      setReturnToPendenteFeedback({
        type: 'error',
        message: err?.message || 'Erro ao retornar OS para pendência.',
      });
    } finally {
      setReturnToPendenteBusy(false);
    }
  }, [order, returnToPendenteBusy, onReturnToPendenteSuccess]);

  const anexosMediaFiles = React.useMemo(
    () => (anexosFiles || []).filter((f) => !f.isAudio),
    [anexosFiles]
  );
  const defeitoAudioFile = React.useMemo(
    () =>
      (anexosFiles || []).find(
        (f) => f.isAudio || String(f.ext || '').toLowerCase() === 'webm'
      ) || null,
    [anexosFiles]
  );

  if (!order) return null;

  const severityLabel = formatSeverityLabel(order?.defeito_severity_level);
  const alertHex = String(order?.color_bg ?? '').trim();
  const alertCode = String(order?.defeito_alert ?? '').trim();
  const slaInfo = getSLAStatus(order.sla);
  const slaMessage = buildSlaStatusMessage(order, slaInfo);
  const slaProgress = getSlaTimeProgress(order);
  const fone2Raw = String(order?.TB02115_FONE2 ?? '').trim();
  const celularRaw = String(order?.TB02115_CELULAR ?? '').trim();
  const hasExtraPhones = fone2Raw !== '' || celularRaw !== '';
  const statusName = order.TB01073_NOME || order.status || 'Não informado';
  const isRetorno =
    codstatusRetorno !== '' && String(order.TB02115_STATUS || '').trim() === String(codstatusRetorno || '').trim();

  const cfgFuturaStatus = String(statusUptodate || '').trim();
  const orderStatusCode = String(order?.TB02115_STATUS || '').trim();
  const standbyNotesTooltipText = String(order?.standbyNotes ?? order?.STANDBY_NOTES ?? '').trim();
  const showStandbyNotesIndicator =
    Boolean(cfgFuturaStatus) && orderStatusCode === cfgFuturaStatus && standbyNotesTooltipText.length > 0;

  const isTecnicoTerritorio = order.TB01024_TERRITORIO === 'S';

  const toggleDefeitoDetails = () => {
    setShowDefeitoDetails((v) => !v);
  };

  const openGoogleMaps = () => {
    const endereco = order.endereco || 'Endereço não informado';
    if (!endereco || endereco === 'Endereço não informado' || !String(endereco).trim()) {
      alert('Endereço não disponível para visualização no mapa.');
      return;
    }
    let enderecoCompleto = endereco;
    const cidade = order.TB02115_CIDADE || order.cidade;
    const estado = order.TB02115_ESTADO;
    if (cidade && !enderecoCompleto.toLowerCase().includes(String(cidade).toLowerCase())) {
      enderecoCompleto += `, ${cidade}`;
    }
    if (estado && !enderecoCompleto.toLowerCase().includes(String(estado).toLowerCase())) {
      enderecoCompleto += `, ${estado}`;
    }
    if (!enderecoCompleto.toLowerCase().includes('brasil')) {
      enderecoCompleto += ', Brasil';
    }
    const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(enderecoCompleto)}/@?entry=ttu`;
    const w = window.open(mapsUrl, '_blank');
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* ignore */
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target !== e.currentTarget) return;
    if (atendimentosHistoricoMounted) {
      closeAtendimentosHistorico();
      return;
    }
    if (onClose) onClose();
  };

  const pedidoBlocks =
    Number(order.pedidos_vinculados) > 1 && linkedPedidosList.length > 0
      ? linkedPedidosList.map((cod) => ({ cod }))
      : order.pedidoVinculado
        ? [{ cod: order.pedidoVinculado }]
        : [];

  const thirdColOpen =
    (showReincidenciaPanel && isOrderReincidencia) ||
    Boolean(pedidoPanelCodigo) ||
    notesPanelOpen;

  const handleTogglePedidoPanel = (cod) => {
    const c = String(cod ?? '').trim();
    if (!c) return;
    setShowReincidenciaPanel(false);
    setNotesPanelOpen(false);
    setPedidoPanelCodigo((prev) => (prev === c ? null : c));
    if (atendimentosHistoricoMounted) closeAtendimentosHistorico();
  };

  const addressLines = parseOrderAddressLines(order);
  const streetAddress = formatOrderStreetAddress(order);

  const defeitoDisplay = getDefeitoDisplayState(defeitoData, motivoDisplay);
  const {
    obsGeralProminent,
    showObsGeralInExpanded,
    obsGeral: obsGeralText,
  } = defeitoDisplay;

  const defeitoToggleName =
    loadingDefeito && !defeitoData
      ? 'Carregando…'
      : obsGeralProminent
        ? 'Sem defeito'
        : defeitoData?.defeito || 'Defeito não informado';

  const viewMode = showAnexosView
    ? 'anexos'
    : showSugestaoSolucao
      ? 'sugestao'
      : showLogsView
        ? 'logs'
        : showHistoricoView
          ? 'historico'
          : 'detail';
  const menuModalOpen =
    showLogsView || showSugestaoSolucao || showHistoricoView || showAnexosView || Boolean(encaminharPanel);
  const reincidenciaActionDisabled = menuModalOpen;

  const canReturnToPendente = Boolean(order?.fromFuturaRetornoLiberado);

  const menuProps = {
    viewMode,
    onSelectLogs: () => {
      setShowSugestaoSolucao(false);
      setShowAnexosView(false);
      closeHistoricoView();
      openLogsView();
    },
    onSelectHistorico: () => {
      setShowSugestaoSolucao(false);
      setShowAnexosView(false);
      closeLogsView();
      openHistoricoView();
    },
    onSelectSugestao: () => {
      closeLogsView();
      closeHistoricoView();
      setShowAnexosView(false);
      setShowSugestaoSolucao(true);
    },
    onSelectAnexos: () => {
      if (showAnexosView) {
        setShowAnexosView(false);
        return;
      }
      closeLogsView();
      closeHistoricoView();
      setShowSugestaoSolucao(false);
      setShowReincidenciaPanel(false);
      setPedidoPanelCodigo(null);
      setNotesPanelOpen(false);
      setShowAnexosView(true);
    },
    canViewAnexos: hasCustomersModule && anexosMediaFiles.length > 0,
    onCloseSubView: showSugestaoSolucao
      ? () => setShowSugestaoSolucao(false)
      : showLogsView
        ? closeLogsView
        : showHistoricoView
          ? closeHistoricoView
          : undefined,
    onEncaminharChamado,
    canEncaminharChamado,
    canReturnToPendente,
    onReturnToPendente: handleReturnToPendente,
    returnToPendenteBusy,
  };

  return (
    <div
      className="modal-overlay order-details-modal-overlay order-details-v2-overlay"
      onClick={handleOverlayClick}
    >
      <div
        className={`order-details-modal-shell order-details-v2-shell${thirdColOpen ? ' order-details-v2-shell--third-col-open' : ''}${atendimentosHistoricoMounted ? ' order-details-v2-shell--atendimentos-backdrop' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="order-details-modal-row">
          <div className="order-details-modal-wrap">
            <div className="order-details-v2-modal">
              <header className="order-details-v2-header">
                <div className="order-details-v2-header-main">
                  <h2 className="order-details-v2-title">OS {order.id}</h2>
                  {showStandbyNotesIndicator ? (
                    <span ref={standbyNotesAnchorRef} className="order-details-standby-notes-popover-anchor">
                      <button
                        type="button"
                        className="order-details-standby-notes-trigger condicoes-th-info-btn"
                        title="Informações sobre agendamento futuro"
                        aria-expanded={standbyNotesPopoverOpen}
                        onClick={(e) => {
                          e.stopPropagation();
                          setStandbyNotesPopoverOpen((v) => !v);
                        }}
                      >
                        <CalendarSearch size={9} strokeWidth={2.25} aria-hidden />
                      </button>
                      {standbyNotesPopoverOpen ? (
                        <div className="condicoes-th-tooltip-popover order-details-standby-notes-tooltip" role="tooltip">
                          {standbyNotesTooltipText}
                        </div>
                      ) : null}
                    </span>
                  ) : null}
                  <div className="order-details-v2-badges">
                    <span className="order-details-v2-badge-tipo-wrap">
                      <Odv2HoverTooltip text="Tipo de ordem de serviço">
                        <span className="order-details-v2-badge order-details-v2-badge--tipo">
                          {tipoOsEdit.tipoLabel}
                        </span>
                      </Odv2HoverTooltip>
                      {tipoOsEdit.canEditTipo ? (
                        <span className="order-details-v2-badge-tipo-edit-wrap" ref={tipoOsEdit.menuRef}>
                          <button
                            type="button"
                            className="odv2-inline-icon-btn order-details-v2-badge-tipo-edit-btn"
                            onClick={() => tipoOsEdit.setMenuOpen((v) => !v)}
                            disabled={tipoOsEdit.isSaving}
                            title="Alterar tipo de OS"
                            aria-label="Alterar tipo de OS"
                            aria-expanded={tipoOsEdit.menuOpen}
                            aria-haspopup="menu"
                          >
                            <Pencil size={14} strokeWidth={2} aria-hidden />
                          </button>
                          {tipoOsEdit.menuOpen ? (
                            <div className="order-details-v2-tipo-os-menu" role="menu">
                              {tipoOsEdit.changeOptions.map((opt) => (
                                <button
                                  key={opt.code}
                                  type="button"
                                  role="menuitem"
                                  className="order-details-v2-tipo-os-menu-item"
                                  disabled={tipoOsEdit.isSaving}
                                  onClick={() => tipoOsEdit.handleSelectTipo(opt.code)}
                                >
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </span>
                      ) : null}
                      {tipoOsEdit.feedback ? (
                        <span
                          className={`order-details-v2-tipo-os-feedback order-details-v2-tipo-os-feedback--${tipoOsEdit.feedback.type}`}
                          role="status"
                        >
                          {tipoOsEdit.feedback.message}
                        </span>
                      ) : null}
                    </span>
                    <Odv2HoverTooltip text="Status atual">
                      <span className="order-details-v2-badge order-details-v2-badge--status">{statusName}</span>
                    </Odv2HoverTooltip>
                    {isRetorno ? (
                      <span className="order-details-v2-badge order-details-v2-badge--retorno">Retorno</span>
                    ) : null}
                    {isOrderReincidencia ? (
                      <span className="order-details-v2-badge-reincidencia-group">
                        <span className="order-details-v2-badge order-details-v2-badge--muted">
                          Reincidência: Sim
                        </span>
                        <button
                          type="button"
                          className={`order-details-v2-badge order-details-v2-badge--reincidencia-action${showReincidenciaPanel ? ' order-details-v2-badge--reincidencia-action--active' : ''}${reincidenciaActionDisabled ? ' order-details-v2-badge--reincidencia-action--disabled' : ''}`}
                          disabled={reincidenciaActionDisabled}
                          onClick={() => {
                            if (reincidenciaActionDisabled) return;
                            setPedidoPanelCodigo(null);
                            setNotesPanelOpen(false);
                            if (atendimentosHistoricoMounted) closeAtendimentosHistorico();
                            setShowReincidenciaPanel((v) => !v);
                          }}
                          aria-expanded={showReincidenciaPanel}
                          aria-controls="order-details-v2-reincidencia-panel"
                        >
                          {showReincidenciaPanel ? (
                            <EyeOff size={12} strokeWidth={2} aria-hidden />
                          ) : (
                            <Eye size={12} strokeWidth={2} aria-hidden />
                          )}
                          Ver reincidência
                        </button>
                      </span>
                    ) : (
                      <span className="order-details-v2-badge order-details-v2-badge--muted">
                        Reincidência: Não
                      </span>
                    )}
                    {order?.source === 'native' && Number(order.returnCount) > 0 ? (
                      <Odv2HoverTooltip text="Ver ciclos no Histórico do processo">
                        <button
                          type="button"
                          className="order-details-v2-badge order-details-v2-badge--retorno order-details-v2-badge--btn"
                          onClick={menuProps.onSelectHistorico}
                          disabled={menuModalOpen}
                        >
                          Retornos: {Number(order.returnCount)}
                        </button>
                      </Odv2HoverTooltip>
                    ) : null}
                  </div>
                </div>
                <OrderDetailsModalV2HeaderMenu {...menuProps} />
              </header>
              {returnToPendenteBusy || returnToPendenteFeedback ? (
                <div
                  className={`odv2-return-pendente-banner${
                    returnToPendenteFeedback
                      ? ` odv2-return-pendente-banner--${returnToPendenteFeedback.type}`
                      : ' odv2-return-pendente-banner--busy'
                  }`}
                  role={returnToPendenteFeedback?.type === 'error' ? 'alert' : 'status'}
                >
                  {returnToPendenteBusy ? (
                    <>
                      <i className="bi bi-arrow-repeat odv2-return-pendente-spin" aria-hidden />
                      <span>Retornando OS para pendência…</span>
                    </>
                  ) : (
                    <span>{returnToPendenteFeedback.message}</span>
                  )}
                </div>
              ) : null}

              <div
                className={`order-details-v2-scroll${showAnexosView ? ' order-details-v2-scroll--subview' : ''}`}
              >
                {showAnexosView ? (
                  <OrderDetailsAnexosModal
                    open={showAnexosView}
                    files={anexosMediaFiles}
                    loading={anexosLoading}
                    error={anexosError}
                  />
                ) : (
                  <>
                <div
                  className={`order-details-v2-sla-banner order-details-v2-sla-banner--${slaInfo.tone}`}
                  role="status"
                >
                  <div className="order-details-v2-sla-banner-inner">
                    <span className="order-details-v2-sla-label">SLA Status</span>
                    <span className="order-details-v2-sla-text">
                      <AlertTriangle size={18} strokeWidth={2} aria-hidden />
                      {slaMessage}
                    </span>
                  </div>
                  <div className="order-details-v2-sla-track-wrap">
                    <div
                      className="order-details-v2-sla-track"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={slaProgress ? Math.round(slaProgress.percent) : 0}
                      aria-label="Progresso do prazo entre abertura e previsão"
                    >
                      <div
                        className="order-details-v2-sla-track-fill"
                        style={{ width: `${slaProgress?.percent ?? 0}%` }}
                      />
                    </div>
                    {order.previsao ? (
                      <OrderDetailsPrevisaoTimer previsaoStr={order.previsao} variant="slaBanner" />
                    ) : null}
                  </div>
                </div>

              <div className="order-details-v2-body">
                  <div
                    className={`order-details-v2-grid${
                      pedidoPanelCodigo
                        ? ' order-details-v2-grid--third-col order-details-v2-grid--pedido-open'
                        : notesPanelOpen
                          ? ' order-details-v2-grid--third-col order-details-v2-grid--notes-open'
                          : thirdColOpen
                            ? ' order-details-v2-grid--third-col'
                            : ''
                    }`}
                >
                    <div className="order-details-v2-col order-details-v2-col--left">
                      <section className="order-details-v2-block">
                        <div className="order-details-v2-block-head">
                          <h3 className="order-details-v2-block-title">
                            <Info size={20} strokeWidth={2} aria-hidden />
                            Detalhes do Atendimento
                          </h3>
                        </div>
                        <div className="order-details-v2-block-body">
                        <div>
                          <span className="order-details-v2-field-label">Cliente</span>
                          <span className="order-details-v2-field-value order-details-v2-field-value--cliente">
                            {getBoardClienteExibicao(order)}
                          </span>
                        </div>
                        <div>
                          <span className="order-details-v2-field-label">Contrato</span>
                          <span className="order-details-v2-mono">{order.contrato || 'Não informado'}</span>
                        </div>
                        <div className="order-details-v2-equip-wrap">
                          <span className="order-details-v2-field-label">Equipamento</span>
                          <div className="order-details-v2-equip-box">
                            <div className="order-details-v2-equip-head">
                              <span className="order-details-v2-equip-model">
                                {order.equipamento || '—'}
                              </span>
                              <button
                                type="button"
                                className={`order-details-v2-equip-historico-trigger${atendimentosHistoricoMounted ? ' order-details-v2-equip-historico-trigger--active' : ''}`}
                                title="Ver histórico de serviço"
                                aria-label="Ver histórico de serviço"
                                aria-expanded={atendimentosHistoricoMounted}
                                onClick={handleHistoricoServicoClick}
                              >
                                {atendimentosHistoricoMounted ? (
                                  <EyeOff size={14} strokeWidth={2} aria-hidden />
                                ) : (
                                  <History size={14} strokeWidth={2} aria-hidden />
                                )}
                                Histórico de serviço
                              </button>
                            </div>
                            <div className="order-details-v2-equip-meta">
                              <Odv2HoverTooltip text="Número de série" placement="right" selectable>
                                <span className="order-details-v2-mono order-details-v2-equip-serie">
                                  {order.numeroSerie || order.TB02115_NUMSERIE || '—'}
                                </span>
                              </Odv2HoverTooltip>
                              <span className="order-details-v2-equip-dot" aria-hidden>
                                ·
                              </span>
                              <Odv2HoverTooltip text="Patrimônio/tag" selectable>
                                <span className="order-details-v2-equip-pat">
                                  {order.patrimonio || order.TB02112_PAT || '—'}
                                </span>
                              </Odv2HoverTooltip>
                            </div>
                          </div>
                        </div>
                        </div>
                      </section>

                      <section className="order-details-v2-block order-details-v2-divider-top">
                        <div className="order-details-v2-block-head">
                          <h3 className="order-details-v2-block-title">
                            <Wrench size={20} strokeWidth={2} aria-hidden />
                            Informações Técnicas
                          </h3>
                        </div>
                        <div className="order-details-v2-block-body">
                        <div className="order-details-v2-tech-grid">
                          <div>
                            <span className="order-details-v2-field-label">Técnico</span>
                            <span className="order-details-v2-field-value order-details-v2-field-value--tech">
                              {order.TB01024_NOME || order.tecnico || 'Não informado'}
                            </span>
                          </div>
                          <div>
                            <span className="order-details-v2-field-label">Técnico Máquina</span>
                            <div className="order-details-v2-tech-value-row">
                              <span className="order-details-v2-field-value order-details-v2-field-value--tech order-details-v2-field-value--upper">
                                {order.TECMAQ || 'Não informado'}
                              </span>
                              {isTecnicoTerritorio ? (
                                <Odv2HoverTooltip text="Ver técnicos do território" placement="right">
                                  <button
                                    type="button"
                                    className="order-details-v2-btn-territorio"
                                    onClick={openTecnicosTerritorioModal}
                                    aria-label="Ver técnicos do território"
                                  >
                                    <LandPlot size={14} strokeWidth={2} aria-hidden />
                                  </button>
                                </Odv2HoverTooltip>
                              ) : null}
                            </div>
                          </div>
                        {pedidoBlocks.map(({ cod }, idx) => {
                          const pedidoOpen =
                            String(pedidoPanelCodigo ?? '').trim() === String(cod ?? '').trim();
                          return (
                          <div className="order-details-v2-field--full" key={`ped-${cod}-${idx}`}>
                            <span className="order-details-v2-field-label">Pedido Vinculado</span>
                            <div className="order-details-v2-pedido-row">
                              <span className="order-details-v2-mono order-details-v2-mono--pedido">
                                {cod}
                                <span className="order-details-v2-pedido-pulse" aria-hidden />
                              </span>
                              <button
                                type="button"
                                className={`order-details-v2-btn-secondary order-details-v2-btn-secondary--pedido${pedidoOpen ? ' order-details-v2-btn-secondary--pedido--active' : ''}`}
                                onClick={() => handleTogglePedidoPanel(cod)}
                                aria-expanded={pedidoOpen}
                                aria-controls="order-details-v2-pedido-panel"
                              >
                                {pedidoOpen ? (
                                  <EyeOff size={12} strokeWidth={2} aria-hidden />
                                ) : (
                                  <Eye size={12} strokeWidth={2} aria-hidden />
                                )}
                                Ver Detalhes
                              </button>
                            </div>
                          </div>
                          );
                        })}
                        {loadingLinkedPedidos ? (
                          <span className="order-details-v2-hint">Carregando pedidos…</span>
                        ) : null}
                        </div>
                        </div>
                      </section>
                    </div>

                    <div className="order-details-v2-col order-details-v2-col--right">
                      <section className="order-details-v2-block order-details-v2-block--motivo">
                        <div className="order-details-v2-block-head">
                          <h3 className="order-details-v2-block-title">
                            <TriangleAlert size={20} strokeWidth={2} aria-hidden />
                            Motivo do Chamado
                          </h3>
                          {severityLabel || (hasDefeitoAlert(order) && alertCode) ? (
                            <div className="order-details-v2-block-head-tags">
                              {severityLabel ? (
                                <Odv2HoverTooltip text="Classificação do defeito">
                                  <span className="order-details-v2-badge order-details-v2-badge--muted order-details-v2-badge--severity">
                                    {severityLabel}
                                  </span>
                                </Odv2HoverTooltip>
                              ) : null}
                              {hasDefeitoAlert(order) && alertCode ? (
                                <Odv2HoverTooltip text="Código de alerta do defeito">
                                  <span
                                    className="order-details-v2-alert-code"
                                    style={
                                      alertHex
                                        ? {
                                            backgroundColor: alertHex,
                                            color: contrastTextOnHex(alertHex),
                                          }
                                        : undefined
                                    }
                                  >
                                    {alertCode}
                                  </span>
                                </Odv2HoverTooltip>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <div className="order-details-v2-block-body">
                          <div className="order-details-v2-defeito-box">
                            <button
                              type="button"
                              className="order-details-v2-defeito-toggle"
                              onClick={toggleDefeitoDetails}
                              aria-expanded={showDefeitoDetails}
                            >
                              <div className="order-details-v2-defeito-toggle-main">
                                <span className="order-details-v2-defeito-toggle-title-row">
                                  <Odv2HoverTooltip
                                    text={obsGeralProminent ? 'Sem defeito cadastrado' : 'Defeito atribuído'}
                                    className="odv2-tooltip-wrap--inline-title"
                                    placement="above"
                                  >
                                    <span
                                      className={`order-details-v2-defeito-toggle-name${obsGeralProminent ? ' order-details-v2-defeito-toggle-name--muted' : ''}`}
                                    >
                                      {defeitoToggleName}
                                    </span>
                                  </Odv2HoverTooltip>
                                  {hasCustomersModule && defeitoAudioFile?.url ? (
                                    <OrderDetailsDefeitoAudio
                                      src={defeitoAudioFile.url}
                                      orderId={order?.id || order?.TB02115_CODIGO}
                                      onTranscribed={(data) => {
                                        const nextObs = data?.observacaoGeral;
                                        if (nextObs == null) return;
                                        setDefeitoData((prev) =>
                                          prev
                                            ? { ...prev, observacaoGeral: nextObs }
                                            : { observacaoGeral: nextObs }
                                        );
                                        setEditObsGeralValue(String(nextObs));
                                        setShowDefeitoDetails(true);
                                      }}
                                    />
                                  ) : null}
                                </span>
                                <div
                                  className="order-details-v2-defeito-toggle-motivo-wrap"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  role="presentation"
                                >
                                  {isMotivoEditing ? (
                                    <div className="order-details-v2-motivo-edit">
                                      <input
                                        type="text"
                                        className="order-details-motivo-input"
                                        value={motivoEditValue}
                                        onChange={(e) =>
                                          setMotivoEditValue(e.target.value.slice(0, MOTIVO_OS_MAX))
                                        }
                                        maxLength={MOTIVO_OS_MAX}
                                        aria-label="Editar motivo da OS"
                                        placeholder="Informar motivo do chamado"
                                      />
                                      <div className="odv2-edit-actions order-details-v2-motivo-edit-actions">
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--check"
                                          title="Salvar"
                                          aria-label="Salvar"
                                          onClick={handleSaveMotivo}
                                          disabled={isSavingMotivo}
                                        >
                                          <i className="bi bi-check-lg" style={{ fontSize: '14px' }} aria-hidden />
                                        </button>
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--cancel"
                                          title="Cancelar"
                                          aria-label="Cancelar"
                                          onClick={handleCancelMotivoEdit}
                                          disabled={isSavingMotivo}
                                        >
                                          <X size={14} strokeWidth={2} aria-hidden />
                                        </button>
                                      </div>
                                    </div>
                                  ) : obsGeralProminent && isObsGeralEditing ? (
                                    <div className="order-details-v2-motivo-edit order-details-v2-obs-geral-inline-edit">
                                      <textarea
                                        className="defeito-edit-textarea order-details-v2-obs-geral-textarea order-details-v2-obs-geral-textarea--inline"
                                        value={editObsGeralValue}
                                        onChange={(e) => setEditObsGeralValue(e.target.value)}
                                        rows={4}
                                        placeholder="Observações da ordem de serviço"
                                        aria-label="Editar observações gerais da ordem de serviço"
                                      />
                                      <div className="odv2-edit-actions order-details-v2-motivo-edit-actions">
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--check"
                                          title="Salvar"
                                          aria-label="Salvar"
                                          onClick={() => handleSaveObsGeral(defeitoData, setDefeitoData)}
                                          disabled={isSavingObsGeral}
                                        >
                                          <i className="bi bi-check-lg" style={{ fontSize: '14px' }} aria-hidden />
                                        </button>
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--cancel"
                                          title="Cancelar"
                                          aria-label="Cancelar"
                                          onClick={() => handleCancelObsGeralEdit(defeitoData)}
                                          disabled={isSavingObsGeral}
                                        >
                                          <X size={14} strokeWidth={2} aria-hidden />
                                        </button>
                                      </div>
                                    </div>
                                  ) : obsGeralProminent ? (
                                    <span className="order-details-v2-defeito-toggle-motivo-row order-details-v2-defeito-toggle-motivo-row--obs-prominent">
                                      <span className="order-details-v2-defeito-toggle-motivo order-details-v2-defeito-toggle-motivo--obs-geral">
                                        {obsGeralText}
                                      </span>
                                      <span className="order-details-v2-defeito-toggle-motivo-actions">
                                        <button
                                          type="button"
                                          className="odv2-inline-icon-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartObsGeralEdit(defeitoData);
                                          }}
                                          title="Editar observações"
                                          aria-label="Editar observações"
                                        >
                                          <Pencil size={14} strokeWidth={2} aria-hidden />
                                        </button>
                                        <button
                                          type="button"
                                          className="odv2-inline-icon-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartMotivoEdit();
                                          }}
                                          title="Informar motivo"
                                          aria-label="Informar motivo"
                                        >
                                          <span className="order-details-v2-motivo-add-label">Motivo</span>
                                        </button>
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="order-details-v2-defeito-toggle-motivo-row">
                                      <span className="order-details-v2-defeito-toggle-motivo">
                                        {motivoDisplay || 'Não informado'}
                                      </span>
                                      <button
                                        type="button"
                                        className="odv2-inline-icon-btn"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleStartMotivoEdit();
                                        }}
                                        title="Editar motivo"
                                        aria-label="Editar motivo"
                                      >
                                        <Pencil size={14} strokeWidth={2} aria-hidden />
                                      </button>
                                    </span>
                                  )}
                                  {obsGeralProminent && obsGeralSaveFeedback ? (
                                    <div
                                      className={`defeito-save-feedback defeito-save-feedback-${obsGeralSaveFeedback.type} order-details-v2-motivo-feedback`}
                                    >
                                      {obsGeralSaveFeedback.message}
                                    </div>
                                  ) : null}
                                  {!obsGeralProminent && motivoSaveFeedback ? (
                                    <div
                                      className={`defeito-save-feedback defeito-save-feedback-${motivoSaveFeedback.type} order-details-v2-motivo-feedback`}
                                    >
                                      {motivoSaveFeedback.message}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                              <span className="order-details-v2-defeito-toggle-action">
                                {showDefeitoDetails ? (
                                  <>
                                    <ChevronUp size={14} strokeWidth={2} aria-hidden />
                                    Ocultar detalhes
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown size={14} strokeWidth={2} aria-hidden />
                                    {obsGeralProminent ? 'Adicionar defeito' : 'Ver detalhes'}
                                  </>
                                )}
                              </span>
                            </button>
                            {showDefeitoDetails ? (
                              <div className="order-details-v2-defeito-table-wrap">
                                {loadingDefeito ? (
                                  <div className="defeito-loading" style={{ padding: 12 }}>Carregando…</div>
                                ) : null}
                                {defeitoError ? <div className="defeito-error">{defeitoError}</div> : null}
                                {defeitoData && !loadingDefeito && !defeitoError ? (
                                  <div className="defeito-table-container">
                                    <table className="defeito-table">
                                      <thead>
                                        <tr>
                                          <th>Defeito</th>
                                          <th>Observação</th>
                                          {!isDefeitoEditing && !isObsEditing ? <th>Solução</th> : null}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td
                                            className={`defeito-table-cell defeito-table-cell-clickable ${isDefeitoEditing ? 'defeito-table-cell-editing' : ''}`}
                                            onClick={() => handleStartDefeitoEdit(defeitoData)}
                                          >
                                            {isDefeitoEditing ? (
                                              <div className="defeito-edit-field">
                                                <input
                                                  type="text"
                                                  value={defeitoSearchQuery}
                                                  onChange={handleDefeitoSearchChange}
                                                  placeholder="Buscar defeito por nome..."
                                                  className="defeito-edit-input"
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                                {isDefeitoSearchLoading ? (
                                                  <div className="defeito-search-status">Buscando...</div>
                                                ) : null}
                                                {defeitoSearchError ? (
                                                  <div className="defeito-search-error">{defeitoSearchError}</div>
                                                ) : null}
                                                {defeitoSearchResults.length > 0 ? (
                                                  <ul className="defeito-search-results">
                                                    {defeitoSearchResults.map((item) => (
                                                      <li key={item.codigo}>
                                                        <button
                                                          type="button"
                                                          className="defeito-search-result-item"
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelectDefeitoResult(item);
                                                          }}
                                                        >
                                                          <span className="defeito-search-result-name">{item.nome}</span>
                                                          {item.solucao ? (
                                                            <span className="defeito-search-result-solucao">
                                                              {item.solucao}
                                                            </span>
                                                          ) : null}
                                                        </button>
                                                      </li>
                                                    ))}
                                                  </ul>
                                                ) : null}
                                              </div>
                                            ) : (
                                              defeitoData.defeito
                                            )}
                                          </td>
                                          <td
                                            className={`defeito-table-cell ${isObsEditing ? 'defeito-table-cell-editing' : 'defeito-table-cell-clickable'}`}
                                            onClick={() => handleStartObsEdit(defeitoData)}
                                          >
                                            {isObsEditing ? (
                                              <textarea
                                                className="defeito-edit-textarea"
                                                value={editObsValue}
                                                onChange={(e) => setEditObsValue(e.target.value)}
                                                rows={4}
                                                placeholder="Editar observação do defeito..."
                                                onClick={(e) => e.stopPropagation()}
                                              />
                                            ) : (
                                              defeitoData.observacao
                                            )}
                                          </td>
                                          {!isDefeitoEditing && !isObsEditing ? (
                                            <td>{defeitoData.solucao}</td>
                                          ) : null}
                                        </tr>
                                      </tbody>
                                    </table>
                                    {isDefeitoEditing || isObsEditing ? (
                                      <div className="odv2-edit-actions">
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--check"
                                          title="Salvar"
                                          aria-label="Salvar"
                                          onClick={() => handleSaveDefeitoObs(defeitoData, setDefeitoData)}
                                          disabled={isSavingDefeitoObs}
                                        >
                                          <i className="bi bi-check-lg" style={{ fontSize: '14px' }} aria-hidden />
                                        </button>
                                        <button
                                          type="button"
                                          className="odv2-icon-btn odv2-icon-btn--cancel"
                                          title="Cancelar"
                                          aria-label="Cancelar"
                                          onClick={() => handleCancelDefeitoObsEdit(defeitoData)}
                                          disabled={isSavingDefeitoObs}
                                        >
                                          <X size={14} strokeWidth={2} aria-hidden />
                                        </button>
                                      </div>
                                    ) : null}
                                    {defeitoSaveFeedback ? (
                                      <div
                                        className={`defeito-save-feedback defeito-save-feedback-${defeitoSaveFeedback.type}`}
                                      >
                                        {defeitoSaveFeedback.message}
                                      </div>
                                    ) : null}
                                    {!isDefeitoEditing && !isObsEditing && showObsGeralInExpanded ? (
                                      <div
                                        className={`order-details-v2-obs-geral${isObsGeralEditing ? ' order-details-v2-obs-geral--editing' : ''}`}
                                      >
                                        <span className="order-details-v2-field-label order-details-v2-field-label--caps">
                                          Observações
                                        </span>
                                        <textarea
                                          className={`defeito-edit-textarea order-details-v2-obs-geral-textarea${isObsGeralEditing ? '' : ' order-details-v2-obs-geral-textarea--readonly defeito-table-cell-clickable'}`}
                                          readOnly={!isObsGeralEditing}
                                          rows={4}
                                          value={
                                            isObsGeralEditing
                                              ? editObsGeralValue
                                              : defeitoData.observacaoGeral || ''
                                          }
                                          placeholder="Nenhuma observação registrada."
                                          aria-label="Observações gerais da ordem de serviço"
                                          onClick={() => {
                                            if (!isObsGeralEditing) handleStartObsGeralEdit(defeitoData);
                                          }}
                                          onChange={(e) => {
                                            if (isObsGeralEditing) setEditObsGeralValue(e.target.value);
                                          }}
                                        />
                                        {isObsGeralEditing ? (
                                          <div className="odv2-edit-actions">
                                            <button
                                              type="button"
                                              className="odv2-icon-btn odv2-icon-btn--check"
                                              title="Salvar"
                                              aria-label="Salvar"
                                              onClick={() => handleSaveObsGeral(defeitoData, setDefeitoData)}
                                              disabled={isSavingObsGeral}
                                            >
                                              <i className="bi bi-check-lg" style={{ fontSize: '14px' }} aria-hidden />
                                            </button>
                                            <button
                                              type="button"
                                              className="odv2-icon-btn odv2-icon-btn--cancel"
                                              title="Cancelar"
                                              aria-label="Cancelar"
                                              onClick={() => handleCancelObsGeralEdit(defeitoData)}
                                              disabled={isSavingObsGeral}
                                            >
                                              <X size={14} strokeWidth={2} aria-hidden />
                                            </button>
                                          </div>
                                        ) : null}
                                        {obsGeralSaveFeedback ? (
                                          <div
                                            className={`defeito-save-feedback defeito-save-feedback-${obsGeralSaveFeedback.type}`}
                                          >
                                            {obsGeralSaveFeedback.message}
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </section>

                      <section className="order-details-v2-block order-details-v2-divider-top">
                        <div className="order-details-v2-block-head">
                          <h3 className="order-details-v2-block-title">
                            <MapPin size={20} strokeWidth={2} aria-hidden />
                            Localização e Contato
                          </h3>
                        </div>
                        <div className="order-details-v2-block-body">
                          <div className="order-details-v2-address-block">
                            <span className="order-details-v2-field-label order-details-v2-field-label--caps">
                              Endereço
                            </span>
                            <div className="order-details-v2-address-street-row">
                              <span className="order-details-v2-field-value">{streetAddress}</span>
                              <button type="button" className="order-details-v2-btn-maps" onClick={openGoogleMaps}>
                                <Map size={16} strokeWidth={2} aria-hidden />
                                Ver no Maps
                              </button>
                            </div>
                            {addressLines.line2 ? (
                              <span className="order-details-v2-address-line2">{addressLines.line2}</span>
                            ) : null}
                            {addressLines.localLine ? (
                              <Odv2HoverTooltip
                                text="Localização interna no endereço"
                                className="odv2-tooltip-wrap--block"
                                placement="above"
                              >
                                <span className="order-details-v2-address-local">{addressLines.localLine}</span>
                              </Odv2HoverTooltip>
                            ) : null}
                          </div>
                          <div className="order-details-v2-equip-wrap" style={{ paddingTop: 8, marginTop: 0 }}>
                            <span className="order-details-v2-field-label order-details-v2-field-label--caps">
                              Solicitante &amp; Contatos
                            </span>
                            <span className="order-details-v2-solicitante">
                              {order.solicitante || 'Não informado'}
                              {solicitanteEmail ? (
                                <>
                                  <span className="order-detail-email-bullet" aria-hidden> • </span>
                                  <a href={`mailto:${solicitanteEmail}`} className="order-detail-solicitante-email-link">
                                    {solicitanteEmail}
                                  </a>
                                </>
                              ) : null}
                            </span>
                            {hasExtraPhones ? (
                              <span className="order-details-v2-phones-extra">
                                {fone2Raw ? (
                                  <span className="order-details-v2-phone-part">
                                    <strong>Fone 2:</strong> {formatPhoneDisplay(fone2Raw)}
                                  </span>
                                ) : null}
                                {fone2Raw && celularRaw ? (
                                  <span className="order-details-v2-phone-sep" aria-hidden>
                                    |
                                  </span>
                                ) : null}
                                {celularRaw ? (
                                  <a
                                    className="order-details-v2-phone-part order-details-v2-phone-wa"
                                    href={`https://wa.me/55${celularRaw.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <strong>WhatsApp:</strong> {formatPhoneDisplay(celularRaw)}
                                    <i className="bi bi-whatsapp order-details-v2-whatsapp-icon" aria-hidden />
                                  </a>
                                ) : null}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </section>
                    </div>
                    {showReincidenciaPanel && isOrderReincidencia ? (
                      <OrderDetailsReincidenciaPanel
                        orderId={order.id}
                        items={reincidenciaItems}
                        loading={loadingReincidencia}
                        error={reincidenciaError}
                      />
                    ) : pedidoPanelCodigo ? (
                      <OrderDetailsPedidoPanel
                        codigoPedido={pedidoPanelCodigo}
                        pedido={pedidoPanelData}
                        statusAtual={pedidoStatusAtual}
                        loading={loadingPedidoPanel}
                        error={pedidoPanelError}
                        native={order?.source === 'native'}
                      />
                    ) : notesPanelOpen ? (
                      <OrderDetailsNotesColPanel
                        notes={orderNotes}
                        loadingNotes={loadingNotes}
                        savingNote={savingNote}
                        notesError={notesError}
                        saveError={saveError}
                        onSubmit={submitNote}
                        onClose={closeNotesPanel}
                        startComposeOpen={notesComposeOnOpen}
                      />
                    ) : null}
                  </div>
              </div>
                  </>
                )}
              </div>

              <OrderDetailsModalV2FloatingFooter
                order={order}
                defeitoData={defeitoData}
                onClose={onClose}
                showAddAnotacaoButton={!hasNotes && !showAnexosView}
                onAddAnotacao={openNotesPanelForCompose}
              />
            </div>

            {atendimentosHistoricoMounted ? (
              <OpenOsV2EquipmentHistoryOverlay
                isOpen={atendimentosHistoricoOpen}
                onClose={closeAtendimentosHistorico}
                serie={order?.numeroSerie ?? order?.TB02115_NUMSERIE ?? ''}
                modelo={order?.equipamento ?? order?.TB02112_PRODUTO ?? ''}
                patrimonio={order?.patrimonio ?? order?.TB02112_PAT ?? ''}
                instalado={order?.dataInstalacao ?? order?.TB02112_DATA ?? null}
              />
            ) : null}

            <OrderDetailsHistoricoProcessoModal
              open={showHistoricoView}
              onClose={closeHistoricoView}
              orderId={order?.id ?? order?.TB02115_CODIGO}
              steps={historicoSteps}
              cycles={historicoCycles}
              loading={loadingHistorico}
              error={historicoError}
            />

            <OrderDetailsAuditLogsModal
              open={showLogsView}
              onClose={closeLogsView}
              orderId={order?.id ?? order?.TB02115_CODIGO}
              logs={auditLogs}
              loading={loadingAuditLogs}
              error={auditLogsError}
            />

            <OrderDetailsSugestaoModal
              open={showSugestaoSolucao}
              onClose={() => setShowSugestaoSolucao(false)}
              orderId={order?.id ?? order?.TB02115_CODIGO}
              loadingDefeito={loadingDefeito}
              defeitoData={defeitoData}
            />

            <OrderDetailsTecnicosTerritorioModal
              open={tecnicosTerritorioModalOpen}
              onClose={closeTecnicosTerritorioModal}
              orderId={order?.id ?? order?.TB02115_CODIGO}
              tecnicos={tecnicosTerritorio}
              loading={loadingTecnicosTerritorio}
              error={tecnicosTerritorioError}
            />

            <OrderDetailsEncaminharChamadoModal
              open={Boolean(encaminharPanel)}
              onClose={onEncaminharPanelClose}
              orderId={encaminharPanel?.orderId}
              areas={encaminharPanel?.areas ?? []}
              onConfirm={onEncaminharConfirm}
            />
          </div>

        </div>
      </div>
    </div>
  );
}
